import { useOutletContext } from 'react-router-dom'

import { Box, Button, Divider, List, Text, Title } from '@factorialco/deprecated-design-system'
import { type CandidateDetailFragment } from 'generated/resources/graphql'
import moment from 'moment'

import DocumentType from 'modules/core/components/DocumentType'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { useCandidateFiles } from 'modules/ats/hooks/candidateFiles'

import { OtherFiles } from './OtherFiles'

interface ApplicationWithCv {
  id: number
  jobPostingId: number
  jobPostingTitle: string
  cv: {
    contentType: string
    filename: string
    url: string
    createdAt: string
  }
}

interface OutletContext {
  candidate: CandidateDetailFragment
}

const CandidateFiles = () => {
  const { navigation } = useNavigation()
  const { candidate } = useOutletContext<OutletContext>()
  const { candidateFiles } = useCandidateFiles({ atsCandidateId: candidate.id })
  const applicationsWithCv = candidate.applications.reduce<ApplicationWithCv[]>(
    (allCvs, application) => {
      const cv = candidateFiles.find(
        (file) =>
          file.atsApplicationId === application.id &&
          file.category === 'cv' &&
          file.authorType === 'candidate'
      )

      if (cv) {
        allCvs.push({
          id: application.id,
          jobPostingId: application.relatedJobPosting.id,
          jobPostingTitle: application.relatedJobPosting.title,
          cv: {
            contentType: cv.file.contentType,
            filename: cv.file.filename,
            url: cv.file.url,
            createdAt: cv.createdAt,
          },
        })
      }

      return allCvs
    },
    []
  )

  return (
    <Box padding="s24" gap="s32">
      <Title
        variant="400"
        illustration="documents"
        title={i18n.t('ats.candidate_details.files.documents.title')}
        description={i18n.t('ats.candidate_details.files.documents.description')}
      />

      <Box>
        {applicationsWithCv.length > 0 ? (
          <List>
            {applicationsWithCv.map((application) => {
              const uploadedAt = moment(application.cv.createdAt).format('ll')
              const uploadedTo = i18n.t('ats.candidate_details.files.uploaded_to', {
                job_posting_title: application.jobPostingTitle,
              })

              return (
                <List.Item key={application.id}>
                  <Box alignItems="center" flexGrow flexDirection="row" gap="s16">
                    <DocumentType mimetype={application.cv.contentType} />
                    <Box overflowX="hidden">
                      <Text.Normal weight="medium" ellipsis>
                        {application.cv.filename}
                      </Text.Normal>
                      <Box paddingTop="s4">
                        <Text.Normal size="200" color="grey600">
                          {uploadedAt} - {uploadedTo}
                        </Text.Normal>
                      </Box>
                    </Box>
                  </Box>
                  <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
                    <PopoverMenu type="list">
                      <PopoverMenu.Trigger>
                        <Button.Decorative
                          type="secondary"
                          aria-label={i18n.t('ats.candidate_details.more_actions')}
                          iconName="ellipsis"
                        />
                      </PopoverMenu.Trigger>
                      <PopoverMenu.Content side="bottom" align="end">
                        <PopoverMenu.HrefOption
                          href={application.cv.url}
                          download={application.cv.filename}
                          target="_blank"
                          title={i18n.t('documents.download')}
                        />
                      </PopoverMenu.Content>
                    </PopoverMenu>

                    <Button.Router
                      type="secondary"
                      aria-label={i18n.t('ats.candidates.candidate_application', {
                        job_application_name: application.jobPostingTitle,
                      })}
                      to={navigation.ats.jobs.jobsId.applications.applicationDetail.fullPath({
                        jobId: application.jobPostingId,
                        application_id: application.id,
                      })}
                      iconName="arrow-right"
                    />
                  </Box>
                </List.Item>
              )
            })}
          </List>
        ) : (
          <Box
            textAlign="center"
            padding="s16"
            paddingTop="s32"
            paddingBottom="s32"
            borderRadius={{ all: 'abs008' }}
            border={{
              all: {
                color: 'grey400',
                width: 's1',
                style: 'solid',
              },
            }}
          >
            <Text.Normal weight="medium" color="grey1000">
              {i18n.t('ats.candidate_details.files.documents.not_files_yet')}
            </Text.Normal>
          </Box>
        )}
      </Box>

      <Divider />

      <OtherFiles candidateId={candidate.id} />
    </Box>
  )
}

export default CandidateFiles
